import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Alert, Space, Typography, Divider, Switch, InputNumber, message, Progress } from 'antd';
import { SaveOutlined, MailOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';

const { Text } = Typography;

export default function EmailSettingsManager() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasPass, setHasPass] = useState(false);
  const [usage, setUsage] = useState(null); // { sentToday, dailyLimit }
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { success, message }

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getEmailSettings();
      if (res.data.success) {
        const s = res.data.settings;
        setHasPass(s.hasPass);
        setUsage(res.data.usage || null);
        form.setFieldsValue({
          host: s.host || 'smtp.gmail.com',
          port: s.port || 587,
          secure: !!s.secure,
          user: s.user || '',
          pass: '',
          dailyLimit: s.dailyLimit ?? 450
        });
      }
    } catch {
      message.error('خطا در بارگذاری تنظیمات ایمیل');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const res = await adminAPI.updateEmailSettings(values);
      if (res.data.success) {
        message.success('تنظیمات ایمیل با موفقیت ذخیره شد');
        loadSettings();
      } else {
        message.error(res.data.message || 'خطا در ذخیره');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'خطا در اتصال');
    } finally {
      setSaving(false);
    }
  };

  // ارسال یک ایمیل آزمایشی با تنظیمات ذخیره‌شده — مستقل از هر waitlist یا
  // دعوت‌نامه‌ای خاص. اگر ارسال آزمایشی هم خطا بدهد، مشکل قطعاً از تنظیمات
  // SMTP (کاربر/پسورد/هاست/پورت) است، نه از آدرس گیرنده‌ای که خودتان وارد کرده‌اید.
  const handleTestSend = async () => {
    if (!testEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail.trim())) {
      message.warning('یک آدرس ایمیل معتبر برای تست وارد کنید');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await adminAPI.testEmailConfig(testEmail.trim());
      setTestResult(res.data);
      if (res.data.success) {
        message.success('ایمیل آزمایشی ارسال شد — صندوق گیرنده را بررسی کنید');
        loadSettings();
      } else {
        message.error(res.data.message || 'ارسال آزمایشی ناموفق بود');
      }
    } catch (e) {
      const data = e?.response?.data;
      setTestResult(data || { success: false, message: 'خطا در اتصال به سرور' });
      message.error(data?.message || 'خطا در اتصال به سرور');
    } finally {
      setTesting(false);
    }
  };

  const usagePercent = usage && usage.dailyLimit
    ? Math.min(100, Math.round((usage.sentToday / usage.dailyLimit) * 100))
    : 0;

  return (
    <div style={{ maxWidth: 640 }}>
      <Alert
        type="info"
        showIcon
        icon={<MailOutlined />}
        style={{ marginBottom: 20 }}
        message="تنظیمات سرویس ایمیل (SMTP) — برای نسخه انگلیسی"
        description={
          <span>
            برای ارسال رایگان می‌توانید از یک حساب Gmail با «App Password» استفاده کنید
            (myaccount.google.com/apppasswords، پس از فعال کردن ورود دومرحله‌ای)، یا از یک سرویس
            رایگان مانند Brevo/SendGrid که آدرس SMTP جایگزین می‌دهند.
            {hasPass && <Text type="success" style={{ marginRight: 8 }}>✓ رمز عبور ذخیره شده است</Text>}
          </span>
        }
      />

      <Card
        loading={loading}
        title={
          <Space>
            <MailOutlined />
            <span>پیکربندی SMTP</span>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          style={{ fontFamily: "'Vazirmatn', sans-serif" }}
        >
          <Form.Item
            name="host"
            label="هاست SMTP"
            help="مثال: smtp.gmail.com یا smtp-relay.brevo.com"
          >
            <Input placeholder="smtp.gmail.com" style={{ fontFamily: 'monospace', direction: 'ltr' }} />
          </Form.Item>

          <Space size="large" style={{ display: 'flex' }}>
            <Form.Item name="port" label="پورت" help="587 برای TLS، 465 برای SSL">
              <InputNumber min={1} max={65535} style={{ fontFamily: 'monospace', direction: 'ltr', width: 140 }} />
            </Form.Item>

            <Form.Item name="secure" label="اتصال امن (SSL/465)" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>

          <Form.Item
            name="user"
            label="ایمیل فرستنده (کاربر SMTP)"
            help="مثلاً یک حساب Gmail مخصوص، یا کاربر SMTP سرویسی مثل Brevo"
          >
            <Input placeholder="your_email@gmail.com" style={{ fontFamily: 'monospace', direction: 'ltr' }} />
          </Form.Item>

          <Form.Item
            name="pass"
            label="رمز عبور / App Password"
            help={
              hasPass
                ? 'رمز عبور ذخیره‌شده وجود دارد. برای تغییر، مقدار جدید وارد کنید. در غیر این‌صورت خالی بگذارید.'
                : 'برای Gmail، رمز عبور اصلی حساب کار نمی‌کند — از App Password استفاده کنید'
            }
          >
            <Input.Password
              placeholder={hasPass ? '••••••••••••• (بدون تغییر)' : 'App Password یا رمز SMTP'}
              style={{ fontFamily: 'monospace', direction: 'ltr' }}
            />
          </Form.Item>

          <Divider orientation="right">
            <Space>
              <InfoCircleOutlined />
              <span>محدودیت ارسال</span>
            </Space>
          </Divider>

          <Form.Item
            name="dailyLimit"
            label="سقف ارسال روزانه"
            help="برای جلوگیری از مسدود شدن حساب SMTP رایگان (Gmail حدود ۵۰۰ در روز). عدد ۰ یعنی بدون محدودیت."
          >
            <InputNumber min={0} max={100000} style={{ fontFamily: 'monospace', direction: 'ltr', width: 180 }} />
          </Form.Item>

          {usage && (
            <div style={{ marginBottom: 20, maxWidth: 320 }}>
              <Text type="secondary">مصرف امروز: {usage.sentToday} از {usage.dailyLimit || '∞'}</Text>
              {usage.dailyLimit > 0 && (
                <Progress
                  percent={usagePercent}
                  size="small"
                  status={usagePercent >= 100 ? 'exception' : 'active'}
                />
              )}
            </div>
          )}

          <Form.Item style={{ marginTop: 24 }}>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={saving}
              size="large"
            >
              ذخیره تنظیمات
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card
        title={
          <Space>
            <MailOutlined />
            <span>تست ارسال ایمیل</span>
          </Space>
        }
        style={{ marginTop: 20 }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          یک ایمیل آزمایشی با تنظیمات ذخیره‌شده‌ی بالا به آدرسی که خودتان وارد می‌کنید ارسال می‌شود —
          مستقل از هر waitlist یا دعوت‌نامه‌ای خاص. اگر اینجا هم خطا بگیرید، چون آدرس گیرنده را خودتان
          همین الان درست وارد کرده‌اید، مشکل قطعاً از تنظیمات SMTP (هاست/پورت/کاربر/رمز) است.
        </Text>
        <Space.Compact style={{ width: '100%', maxWidth: 420 }}>
          <Input
            placeholder="you@example.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            style={{ fontFamily: 'monospace', direction: 'ltr' }}
          />
          <Button type="primary" loading={testing} onClick={handleTestSend}>
            ارسال ایمیل آزمایشی
          </Button>
        </Space.Compact>

        {testResult && (
          <Alert
            style={{ marginTop: 16 }}
            type={testResult.success ? 'success' : 'error'}
            showIcon
            message={testResult.success ? 'ارسال موفق' : 'ارسال ناموفق'}
            description={testResult.message}
          />
        )}
      </Card>
    </div>
  );
}
